import { colors } from '@fs/zion-ui'

/**
 * Two-way hover highlighting between the full text transcript and the image.
 *
 * - Hover a transcript word: its rects are drawn on the image.
 * - Hover a word on the image: its rect is drawn and the transcript word is
 *   called out, scrolled into view only if it is outside the scroll port.
 * - Select transcript text: every selected word is drawn on the image, and
 *   hovering one of those on the image calls out its transcript word too.
 * - Touch has no hover, so a tap stands in for it: tap a word on the image to
 *   draw it and call it out, tap anywhere else to clear it. Tapping a
 *   transcript word already works, because the browser follows a tap with a
 *   mouseover on the word.
 *
 * Deliberately outside React. A hover is a rectangle over the image and a class
 * on a word; neither needs component state. Routing hovers through the viewer's
 * highlight store measured 144-304ms from mouseover to a visible rect on a 1167
 * word image, nearly all of it React reconciling, against about 2ms for direct
 * DOM writes. The image to transcript direction could not go through React at
 * all: the transcript's spans are memoised on r2State.activeFieldId, so driving
 * it that way would rebuild every word on every pointermove.
 *
 * Hit testing works on plain rect data, so there is no element per word. A scan
 * of ~1300 bounding boxes is well under a millisecond, and pointermoves are
 * coalesced to one pass per animation frame.
 */

export const IMAGE_HOVER_WORD_CLASS = 'fsImageHoverWord'

// Matches a hovered Zion RecordHighlight: a translucent blue50 tint on the
// stage, plus a solid blue50 box in the viewer's highlight colour base, which
// uses mix-blend-mode: color to turn the ink underneath blue.
// Both sit under the viewer's yellow keyword highlights, so a selection or a word
// hover that covers a keyword leaves it yellow: the tint has no z-index and its layer
// comes before the highlights, and the colour boxes go first in the colour base.
const TINT_BOX_CSS = `position:absolute;pointer-events:none;background:${colors.blue50};opacity:0.2;`
const COLOR_BOX_CSS = `position:absolute;pointer-events:none;background:${colors.blue50};`

// Forced-colors (Windows high contrast) mode, and nothing else. There the browser
// forces the tint to the page colour, so the boxes faded to a faint grey and a
// hovered word could not be found. Zion's RecordHighlight has the same problem
// (FSS-15918); until it is fixed there, these boxes draw a system Highlight ring
// in that mode only, and look like a RecordHighlight everywhere else. A selection
// is dashed so it still differs from a hover without colour. The colour boxes only
// recolour the ink, which forced colors cannot show, so they are hidden.
// Inline styles beat a stylesheet, hence !important.
const BOX_CLASS = 'fsHoverOverlayBox'
// One screen pixel in the stage's own units: the stage is scaled by the viewer's
// zoom, about 0.5 at fit to screen on a large scan, so a plain 2px ring drew under a
// pixel wide. See setScreenPixel.
const SCREEN_PX_VAR = '--fs-hover-screen-px'
const PX = `var(${SCREEN_PX_VAR},1px)`
const FORCED_COLORS_CSS =
  `@media (forced-colors:active){` +
  `.${BOX_CLASS}{forced-color-adjust:none;opacity:1!important;background:transparent!important;` +
  `box-shadow:0 0 0 calc(2 * ${PX}) Highlight,0 0 0 calc(3 * ${PX}) Canvas;}` +
  `.${BOX_CLASS}[data-kind="selection"]{box-shadow:0 0 0 calc(3 * ${PX}) Canvas;` +
  `outline:calc(2 * ${PX}) dashed Highlight;}` +
  `.${BOX_CLASS}[data-color-box]{display:none!important;}}`

let styleInjected = false
function injectStyleOnce() {
  if (styleInjected) return
  styleInjected = true
  const style = document.createElement('style')
  style.textContent = FORCED_COLORS_CSS
  document.head.appendChild(style)
}

// key ('main' | 'split') -> { element, colorBaseRef, viewerRef, source, pools }
// pools: { hover: { tint: [], color: [] }, selection: { tint: [], color: [] } }. Separate
// pools so a hover can never recycle the boxes a selection is using.
// The boxes belong to the entry: the viewer replaces the layer element on every
// image change, and boxes kept anywhere else would outlive it and be painted
// into a detached node.
const layers = new Map()
// key -> [{ fieldId, x, y, width, height, draw }]. Owned by the transcript, so it
// survives the layer being replaced. draw is false for a keyword the viewer's own
// highlight store already draws on hover; a hit on it only calls out the word.
const rectIndex = new Map()
// key -> Map(fieldId -> [rect]) for every word on the image, keywords included, so a
// selection can draw all of them. Hit testing uses rectIndex, which leaves out the
// keywords the viewer's own highlight store already hovers.
const wordRects = new Map()
// key -> [rect] drawn for the current selection, hit tested after rectIndex.
const selectionRects = new Map()

let transcriptRoot = null
let activeWord = null
let activeFieldId = null
let listening = false
let frame = null
let lastPoint = null
let selectionFrame = null
let watchingSelection = false
let touchStart = null

// A finger that moves further than this, or stays down longer, is panning, zooming or
// long-pressing, not tapping.
const TAP_SLOP_PX = 10
const TAP_MAX_MS = 500
// A word at fit-to-screen zoom on a phone is only a few pixels tall, so a tap counts
// if it lands this close to one.
const TOUCH_HIT_PX = 12

const NO_FIELD_IDS = new Set()

const asPercent = (n) => `${n * 100}%`

/* ------------------------------------------------------------------ *
 * drawing
 * ------------------------------------------------------------------ */

function makeBox(cssText, parent, kind) {
  injectStyleOnce()
  const box = document.createElement('div')
  box.dataset.testid = kind === 'selection' ? 'selection-overlay-box' : 'hover-overlay-box'
  box.className = BOX_CLASS
  box.dataset.kind = kind
  if (cssText === COLOR_BOX_CSS) box.dataset.colorBox = ''
  box.style.cssText = cssText
  box.hidden = true
  parent.prepend(box)
  return box
}

function placeBox(box, rect) {
  if (!box) return
  if (!rect) {
    box.hidden = true
    return
  }
  box.style.left = asPercent(rect.x)
  box.style.top = asPercent(rect.y)
  box.style.width = asPercent(rect.width)
  box.style.height = asPercent(rect.height)
  box.hidden = false
}

// Keep the forced-colors ring a fixed width on screen whatever the zoom.
function setScreenPixel(layer) {
  const axes = layerAxes(layer)
  const layoutWidth = layer.element.offsetWidth
  if (!axes || !layoutWidth) return
  const screenPx = `${layoutWidth / Math.hypot(axes.ex.x, axes.ex.y)}px`
  if (screenPx === layer.screenPx) return
  layer.screenPx = screenPx
  layer.element.style.setProperty(SCREEN_PX_VAR, screenPx)
}

const isForcedColors = () => Boolean(window.matchMedia?.('(forced-colors: active)').matches)
const hasVisibleBox = (layer) => [...layer.pools.hover.tint, ...layer.pools.selection.tint].some((box) => !box.hidden)

// A paint sets the ring width, but a box can stay up while the viewer zooms - a selection
// for as long as it lasts. The viewer gives no zoom callback: it animates a style transform
// on an element between it and the layer. So in forced colors, and only while a box is
// showing, those elements' styles are watched and the width is re-measured when one changes.
function watchRingWidth(layer) {
  if (!isForcedColors() || !hasVisibleBox(layer)) {
    layer.ringObserver?.disconnect()
    layer.ringObserver = null
    return
  }
  if (layer.ringObserver) return
  layer.ringObserver = new MutationObserver(() => setScreenPixel(layer))
  const viewer = layer.viewerRef?.current
  for (let el = layer.element.parentElement; el; el = el.parentElement) {
    layer.ringObserver.observe(el, { attributes: true, attributeFilter: ['style'] })
    if (el === viewer) break
  }
}

// Boxes are pooled and only ever hidden, never removed, so a hover is a handful
// of style writes. A word rarely spans more than two rects.
function paint(key, rects, kind = 'hover') {
  const layer = layers.get(key)
  if (!layer) return
  const pool = layer.pools[kind]
  const colorBase = layer.colorBaseRef?.current
  if (rects.length && isForcedColors()) setScreenPixel(layer)
  while (pool.tint.length < rects.length) pool.tint.push(makeBox(TINT_BOX_CSS, layer.element, kind))
  if (colorBase) {
    while (pool.color.length < rects.length) pool.color.push(makeBox(COLOR_BOX_CSS, colorBase, kind))
  }
  pool.tint.forEach((box, i) => placeBox(box, rects[i]))
  pool.color.forEach((box, i) => placeBox(box, rects[i]))
  watchRingWidth(layer)
}

/**
 * Draw the given highlights on a viewer. Positions are 0..1 image ratios, the
 * same space the layer covers, so zoom and pan need no maths here.
 * @param {string} key - 'main' or 'split'
 * @param {Array} highlights - highlight objects carrying a `position`
 * @param {string} [source] - 'transcript' or 'image': who asked for the rect
 */
export function showHoverRects(key, highlights, source = 'transcript') {
  const layer = layers.get(key)
  if (!layer) return
  layer.source = source
  paint(key, (highlights || []).map((h) => h?.position).filter(Boolean))
}

/**
 * Hide every hover rect on a viewer.
 * @param {string} key - 'main' or 'split'
 */
export function hideHoverRects(key) {
  const layer = layers.get(key)
  if (!layer) return
  layer.source = null
  paint(key, [])
}

/* ------------------------------------------------------------------ *
 * image -> transcript
 * ------------------------------------------------------------------ */

function clearActiveWord() {
  activeWord?.classList.remove(IMAGE_HOVER_WORD_CLASS)
  activeWord = null
  activeFieldId = null
}

function findScrollPort(el) {
  let node = el?.parentElement
  while (node) {
    const { overflowY } = getComputedStyle(node)
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) return node
    node = node.parentElement
  }
  return null
}

// Scroll only when the word is out of view: scrolling on every move would make
// the panel lurch under the reader.
function revealIfHidden(el) {
  const scroller = findScrollPort(transcriptRoot)
  if (!scroller) return
  const box = el.getBoundingClientRect()
  const port = scroller.getBoundingClientRect()
  if (box.bottom < port.top || box.top > port.bottom) {
    el.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }
}

function calloutWord(fieldId) {
  if (fieldId === activeFieldId) return
  clearActiveWord()
  if (!transcriptRoot) return
  const escaped = String(fieldId).replace(/(["\\])/g, String.raw`\$1`)
  const el = transcriptRoot.querySelector(`[data-field-id="${escaped}"]`)
  if (!el) return
  el.classList.add(IMAGE_HOVER_WORD_CLASS)
  activeWord = el
  activeFieldId = fieldId
  revealIfHidden(el)
}

// slopX and slopY widen every rect, as image ratios, for a fingertip.
function hitTestRects(rects, x, y, slopX = 0, slopY = 0) {
  // Last match wins, so the topmost of overlapping rects is chosen.
  return (rects || []).reduce(
    (found, r) =>
      x >= r.x - slopX && x <= r.x + r.width + slopX && y >= r.y - slopY && y <= r.y + r.height + slopY ? r : found,
    null
  )
}

// Selected words count too: a selected keyword is not in rectIndex, but it is part of
// the selection and should call out its transcript word like any other.
function hitTest(key, x, y, slopX = 0, slopY = 0) {
  const near = (sx, sy) =>
    hitTestRects(rectIndex.get(key), x, y, sx, sy) || hitTestRects(selectionRects.get(key), x, y, sx, sy)
  // An exact hit beats a near one, so a fingertip between two words picks the one it is on.
  return near(0, 0) || (slopX || slopY ? near(slopX, slopY) : null)
}

// The layer sits in the viewer's zoomed and panned stage, so its bounding box is the
// transformed one and is not clipped by the viewer's overflow: zoomed in, it reaches under
// the transcript, the toolbar and the other split viewer. Only a pointer whose target is
// inside the viewer itself is over the visible image.
function isOverViewer(layer, target) {
  const viewer = layer.viewerRef?.current
  return !viewer || (target instanceof Node && viewer.contains(target))
}

/**
 * Where the layer's corners are on screen: an origin and its x and y axes.
 *
 * The layer sits inside the viewer's transformed stage. Once the image is rotated
 * its bounding box is the upright box around a turned rectangle, so hit testing
 * against it kept answering for the unrotated image. A zero-size element's
 * bounding box is just its transformed position, so three of them pinned to the
 * layer's corners give the whole transform - rotation, flip, zoom and pan -
 * without parsing CSS transforms up the tree.
 */
function layerAxes(layer) {
  const [origin, xEnd, yEnd] = layer.anchors.map((anchor) => anchor.getBoundingClientRect())
  const ex = { x: xEnd.left - origin.left, y: xEnd.top - origin.top }
  const ey = { x: yEnd.left - origin.left, y: yEnd.top - origin.top }
  const det = ex.x * ey.y - ex.y * ey.x
  if (det) return { origin: { x: origin.left, y: origin.top }, ex, ey, det }
  // No layout to measure the anchors with (jsdom): fall back to the upright box.
  const box = layer.element.getBoundingClientRect()
  if (!box.width || !box.height) return null
  return {
    origin: { x: box.left, y: box.top },
    ex: { x: box.width, y: 0 },
    ey: { x: 0, y: box.height },
    det: box.width * box.height,
  }
}

// A screen point as 0..1 image ratios, by inverting the layer's transform.
function toImageRatio({ origin, ex, ey, det }, clientX, clientY) {
  const dx = clientX - origin.x
  const dy = clientY - origin.y
  return { x: (dx * ey.y - dy * ey.x) / det, y: (ex.x * dy - ex.y * dx) / det }
}

function createAnchors(element) {
  return ['left:0;top:0', 'left:100%;top:0', 'left:0;top:100%'].map((place) => {
    const anchor = document.createElement('div')
    anchor.dataset.hoverAnchor = ''
    anchor.style.cssText = `position:absolute;width:0;height:0;pointer-events:none;${place}`
    element.appendChild(anchor)
    return anchor
  })
}

function processPoint() {
  frame = null
  if (!lastPoint) return
  const { clientX, clientY, target, touch } = lastPoint
  let hitFieldId = null
  layers.forEach((layer, key) => {
    const axes = isOverViewer(layer, target) ? layerAxes(layer) : null
    const point = axes && toImageRatio(axes, clientX, clientY)
    const inside = point && point.x >= 0 && point.x <= 1 && point.y >= 0 && point.y <= 1
    // On screen lengths of the image's own axes, which a quarter turn swaps.
    const slopX = touch && axes ? TOUCH_HIT_PX / Math.hypot(axes.ex.x, axes.ex.y) : 0
    const slopY = touch && axes ? TOUCH_HIT_PX / Math.hypot(axes.ey.x, axes.ey.y) : 0
    const hit = inside ? hitTest(key, point.x, point.y, slopX, slopY) : null
    if (hit) hitFieldId = hit.fieldId
    if (hit && hit.draw !== false) {
      showHoverRects(key, [{ position: hit }], 'image')
    } else if (layer.source === 'image') {
      // Only take down a rect the image drew. One the transcript drew for the
      // word under the pointer has to survive moves across the transcript.
      hideHoverRects(key)
    }
  })
  if (hitFieldId) calloutWord(hitFieldId)
  else clearActiveWord()
}

// On the document rather than the viewer: the layer must not take pointer
// events or it would swallow the viewer's pan and zoom, and the image is not
// guaranteed to be inside the layer's parent. Passive, read only.
function onPointerMove(event) {
  // A moving finger is panning or zooming the image. Hovering the words it drags
  // across would flash rects all over the page, and leave one behind when it lifts.
  if (event.pointerType === 'touch') return
  lastPoint = { clientX: event.clientX, clientY: event.clientY, target: event.target }
  if (!frame) frame = requestAnimationFrame(processPoint)
}

function onPointerDown(event) {
  touchStart =
    event.pointerType === 'touch' && event.isPrimary
      ? { x: event.clientX, y: event.clientY, time: event.timeStamp }
      : null
}

function onPointerCancel() {
  touchStart = null
}

// A tap is processed at once rather than on the next frame, so it lands before the
// mouseover the browser sends after it, and a tap on a transcript word is not undone.
function onPointerUp(event) {
  const start = touchStart
  touchStart = null
  if (!start || event.pointerType !== 'touch') return
  const moved = Math.hypot(event.clientX - start.x, event.clientY - start.y)
  if (moved > TAP_SLOP_PX || event.timeStamp - start.time > TAP_MAX_MS) return
  if (frame) cancelAnimationFrame(frame)
  lastPoint = { clientX: event.clientX, clientY: event.clientY, target: event.target, touch: true }
  processPoint()
}

const POINTER_LISTENERS = [
  ['pointermove', onPointerMove],
  ['pointerdown', onPointerDown],
  ['pointerup', onPointerUp],
  ['pointercancel', onPointerCancel],
]

// Listen only while there is something to hit test, so every other view pays
// nothing for this.
function syncListener() {
  const hasRects = [...rectIndex.values(), ...selectionRects.values()].some((r) => r.length)
  const wanted = Boolean(transcriptRoot) && layers.size > 0 && hasRects
  if (wanted === listening) return
  listening = wanted
  if (wanted) {
    POINTER_LISTENERS.forEach(([type, listener]) => document.addEventListener(type, listener, { passive: true }))
    return
  }
  POINTER_LISTENERS.forEach(([type, listener]) => document.removeEventListener(type, listener))
  if (frame) cancelAnimationFrame(frame)
  frame = null
  lastPoint = null
  touchStart = null
  clearActiveWord()
}

/**
 * Give a viewer the rects it can hit test against. Data only, no DOM.
 * @param {string} key - 'main' or 'split'
 * @param {Array} highlights - highlight objects carrying `fieldId` and `position`
 * @param {Set<string>} [drawnByViewer] - fieldIds the viewer draws itself on hover: a hit
 *   on one calls out the transcript word without drawing a second rect over the viewer's
 */
export function setHitTestRects(key, highlights, drawnByViewer = NO_FIELD_IDS) {
  const rects = (highlights || [])
    .filter((h) => h?.fieldId && h?.position)
    .map(({ fieldId, position: { x, y, width, height } }) => ({
      fieldId,
      x,
      y,
      width,
      height,
      draw: !drawnByViewer.has(fieldId),
    }))
  rectIndex.set(key, rects)
  // The words behind any current hover may be gone, e.g. after an image switch.
  hideHoverRects(key)
  clearActiveWord()
  syncListener()
}

/* ------------------------------------------------------------------ *
 * transcript selection -> image
 * ------------------------------------------------------------------ */

// The words the current selection touches, partly selected ones included, which is
// what a reader expects after dragging across the middle of a phrase.
function selectedFieldIds() {
  const ids = new Set()
  const selection = transcriptRoot && window.getSelection?.()
  if (!selection || selection.isCollapsed || !selection.rangeCount) return ids
  const ranges = []
  for (let i = 0; i < selection.rangeCount; i++) {
    const range = selection.getRangeAt(i)
    if (range.intersectsNode(transcriptRoot)) ranges.push(range)
  }
  if (!ranges.length) return ids
  transcriptRoot.querySelectorAll('[data-field-id]').forEach((word) => {
    if (ranges.some((range) => range.intersectsNode(word))) ids.add(word.dataset.fieldId)
  })
  return ids
}

function paintSelection(key, ids) {
  const byFieldId = wordRects.get(key)
  const rects = []
  if (byFieldId) ids.forEach((id) => byFieldId.get(id)?.forEach((rect) => rects.push(rect)))
  selectionRects.set(key, rects)
  paint(key, rects, 'selection')
}

function applySelection() {
  selectionFrame = null
  const ids = selectedFieldIds()
  layers.forEach((_layer, key) => paintSelection(key, ids))
  syncListener()
}

// selectionchange fires on every step of a drag, so redraw at most once per frame.
function onSelectionChange() {
  if (!selectionFrame) selectionFrame = requestAnimationFrame(applySelection)
}

function watchSelection(watch) {
  if (watch === watchingSelection) return
  watchingSelection = watch
  if (watch) {
    document.addEventListener('selectionchange', onSelectionChange)
    return
  }
  document.removeEventListener('selectionchange', onSelectionChange)
  if (selectionFrame) cancelAnimationFrame(selectionFrame)
  selectionFrame = null
}

/**
 * Give a viewer the rects of every word on its image, keywords included, for drawing
 * a transcript selection. Data only, no DOM.
 * @param {string} key - 'main' or 'split'
 * @param {Array} highlights - highlight objects carrying `fieldId` and `position`
 */
export function setWordRects(key, highlights) {
  const byFieldId = new Map()
  ;(highlights || []).forEach((h) => {
    if (!h?.fieldId || !h?.position) return
    if (!byFieldId.has(h.fieldId)) byFieldId.set(h.fieldId, [])
    // fieldId travels with the rect, so a hit on a selected word knows which word it is.
    byFieldId.get(h.fieldId).push({ fieldId: h.fieldId, ...h.position })
  })
  wordRects.set(key, byFieldId)
  paintSelection(key, selectedFieldIds())
  syncListener()
}

/**
 * Register the element holding the transcript's [data-field-id] words.
 * @param {HTMLElement|null} element
 */
export function registerTranscript(element) {
  transcriptRoot = element
  watchSelection(Boolean(element))
  if (!element) {
    clearActiveWord()
    // The viewer can outlive the transcript (fullText -> index keeps the same layer), and
    // nothing would take down a rect drawn for a word that is no longer on screen.
    layers.forEach((_layer, key) => {
      hideHoverRects(key)
      paintSelection(key, new Set())
    })
  }
  syncListener()
}

/**
 * Register, or clear, the positioned layer a viewer draws hover rects into.
 * @param {string} key - 'main' or 'split'
 * @param {HTMLElement|null} element - absolutely positioned over the image
 * @param {{ current: HTMLElement|null }} [colorBaseRef] - the viewer's highlight colour base
 * @param {{ current: HTMLElement|null }} [viewerRef] - the viewer element that clips the image
 */
export function registerHoverLayer(key, element, colorBaseRef, viewerRef) {
  const existing = layers.get(key)
  // The colour base outlives the layer, so its boxes have to be taken out by hand.
  existing?.pools.hover.color.forEach((box) => box.remove())
  existing?.pools.selection.color.forEach((box) => box.remove())
  existing?.ringObserver?.disconnect()
  layers.delete(key)
  if (element) {
    const pools = { hover: { tint: [], color: [] }, selection: { tint: [], color: [] } }
    layers.set(key, { element, colorBaseRef, viewerRef, source: null, pools, anchors: createAnchors(element) })
    // A transcript selection outlives an image change, and selectionchange will not fire
    // again on its own, so the new layer draws it straight away.
    paintSelection(key, selectedFieldIds())
  }
  syncListener()
}
